"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import VadImage from "vadimages-nextjs-image-optimizer";

interface VimeoBackgroundProps {
  videoId: number;
  className?: string;
  imageDirectory?: string;
  isMobile?: boolean;
  needsThumbnail?: boolean;
  thumbnailName?: string;
  thumbnailClassName?: string;
  isVideoUnlisted?: boolean;
  unlistedPrivacyHash?: string;
  link?: string;
  needsRescaling?: boolean;
  onVideoStarted?: () => void;
}

export const VimeoBackground = ({
  videoId,
  className,
  imageDirectory,
  isMobile = false,
  needsThumbnail = false,
  thumbnailName = "hero-thumbnail.jpg",
  isVideoUnlisted = false,
  unlistedPrivacyHash = "",
  link = "",
  needsRescaling = false,
  onVideoStarted,
  thumbnailClassName = "",
}: VimeoBackgroundProps) => {
  const vimeoRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const hasStartedRef = useRef(false);

  const [backgroundScale, setBackgroundScale] = useState<number>(1);
  const [scaleOffset, setScaleOffset] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Debounced resize calculation with requestAnimationFrame for smooth updates
  const recalculateSizes = useCallback(() => {
    if (!needsRescaling || !vimeoRef.current || !containerRef.current) return;

    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const children = Array.from(vimeoRef.current?.children ?? []);

      children.forEach((child) => {
        if (child.scrollHeight > 0 && containerRef.current) {
          const scaleCoefficient =
            containerRef.current.scrollHeight / child.scrollHeight;

          if (window.innerWidth >= 768 && window.innerWidth < 1440) {
            setScaleOffset(
              (child.scrollHeight / 2) * scaleCoefficient +
                (containerRef.current.scrollHeight - child.scrollHeight) / 2
            );
            setBackgroundScale(scaleCoefficient);
          } else {
            setScaleOffset(child.scrollHeight / 2);
            setBackgroundScale(1);
          }
        }
      });
    });
  }, [needsRescaling]);

  // Handle window resize with debouncing
  useEffect(() => {
    if (!needsRescaling) return;

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        recalculateSizes();
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [needsRescaling, recalculateSizes]);

  // Initialize Vimeo player with proper error handling and cleanup
  useEffect(() => {
    if (!vimeoRef.current) return;

    let isMounted = true;
    let player: Player | null = null;

    const initPlayer = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        const videoUrl = isVideoUnlisted
          ? {
              url: `https://vimeo.com/${videoId}/${unlistedPrivacyHash}` as
                | `https://vimeo.com/${string}`,
            }
          : { id: videoId };

        player = new Player(vimeoRef.current!, {
          ...videoUrl,
          muted: true,
          background: true,
          loop: true,
          autoplay: true,
          responsive: true,
          // Safari-specific optimizations
          playsinline: true,
          // Quality settings for better mobile performance
          quality: "auto",
        });

        playerRef.current = player;

        // Handle player ready event
        player
          .ready()
          .then(() => {
            if (!isMounted) return;
            setIsLoading(false);
            retryCountRef.current = 0;
          })
          .catch((error) => {
            console.error("Vimeo player ready error:", error);
            if (isMounted) {
              handlePlayerError();
            }
          });

        // Handle play event
        player.on("play", () => {
          if (!hasStartedRef.current) {
            hasStartedRef.current = true;
            onVideoStarted?.();

            // Recalculate sizes after video starts playing
            if (needsRescaling) {
              setTimeout(() => recalculateSizes(), 100);
              setTimeout(() => recalculateSizes(), 500);
              setTimeout(() => recalculateSizes(), 1000);
            }
          }
        });

        // Handle loaded event
        player.on("loaded", () => {
          if (!isMounted) return;
          setIsLoading(false);
          if (needsRescaling) {
            recalculateSizes();
          }
        });

        // Handle error events
        player.on("error", (error) => {
          console.error("Vimeo player error:", error);
          if (isMounted) {
            handlePlayerError();
          }
        });

        // Attempt to play (important for Safari)
        player.play().catch((error) => {
          // Autoplay was prevented, this is common on Safari
          // The video will play when user interacts with page
          console.warn("Autoplay prevented:", error);
        });
      } catch (error) {
        console.error("Error initializing Vimeo player:", error);
        if (isMounted) {
          handlePlayerError();
        }
      }
    };

    const handlePlayerError = () => {
      retryCountRef.current++;

      // Retry up to 3 times with exponential backoff
      if (retryCountRef.current <= 3) {
        const retryDelay = Math.min(
          1000 * Math.pow(2, retryCountRef.current - 1),
          5000
        );
        console.log(
          `Retrying Vimeo player initialization (attempt ${retryCountRef.current}) in ${retryDelay}ms`
        );

        setTimeout(() => {
          if (isMounted) {
            initPlayer();
          }
        }, retryDelay);
      } else {
        setHasError(true);
        setIsLoading(false);
      }
    };

    // Initialize player
    initPlayer();

    // Cleanup function
    return () => {
      isMounted = false;
      hasStartedRef.current = false;

      if (player) {
        player.off("play");
        player.off("loaded");
        player.off("error");
        player.destroy().catch((error) => {
          console.error("Error destroying Vimeo player:", error);
        });
      }

      playerRef.current = null;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [
    videoId,
    isVideoUnlisted,
    unlistedPrivacyHash,
    needsRescaling,
    recalculateSizes,
    onVideoStarted,
  ]);

  return (
    <div className="w-full h-full" ref={containerRef}>
      {needsThumbnail && (
        <div
          className={`absolute z-10 w-full top-1/2 -translate-y-1/2 md:max-lg:hidden ${className}`}
        >
          <VadImage
            src={`${imageDirectory}${
              !!isMobile ? "mobile/" : ""
            }${thumbnailName}`}
            alt={"thumbnail"}
            width={1392}
            height={500}
            className={thumbnailClassName}
            priority
          />
        </div>
      )}

      <div
        className={`absolute z-[20] w-full top-1/2 ${
          !needsRescaling ? "-translate-y-1/2" : ""
        } ${className} transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
        style={
          needsRescaling
            ? {
                transform: `scale(${backgroundScale}) translateY(-50%)`,
                top: `${scaleOffset}px`,
              }
            : {}
        }
      >
        {!!link && (
          <a
            href={link}
            className="absolute top-0 left-0 w-full h-full z-50"
            aria-label="Video link"
          ></a>
        )}
        <div ref={vimeoRef} className={className} />
      </div>

      {/* Error state fallback */}
      {hasError && needsThumbnail && (
        <div
          className={`absolute z-[15] w-full top-1/2 -translate-y-1/2 ${className}`}
        >
          <VadImage
            src={`${imageDirectory}${
              !!isMobile ? "mobile/" : ""
            }${thumbnailName}`}
            alt={"video thumbnail"}
            width={1392}
            height={500}
            className={thumbnailClassName}
            priority
          />
        </div>
      )}
    </div>
  );
};
